/**
 * Chat Hook
 * Manages chat state and API interactions
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../services/api';
import { PlatformType } from '../types/platform.types';
import { ChartData } from '../components/chat/ChartRenderer';
import { AccessRestricted } from '../types/access-requests.types';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  thinkingSteps?: any[];
  toolsUsed?: string[];
  confidence?: number;
  processingTime?: number;
  timestamp: Date;
  chartData?: ChartData;
  accessRestricted?: AccessRestricted;
  isStreaming?: boolean;
}

interface StreamingState {
  isStreaming: boolean;
  currentStep?: string;
  stepNumber?: number;
}

interface SendMessageParams {
  content: string;
  attachments?: File[];
  voiceBlob?: Blob;
  selectedPlatforms?: PlatformType[];
}

export function useChat(sessionId?: string) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | undefined>(sessionId);
  const [streamingState, setStreamingState] = useState<StreamingState>({ isStreaming: false });
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load messages for session (only if messages are empty)
  useEffect(() => {
    if (currentSessionId && messages.length === 0) {
      loadMessages();
    }
  }, [currentSessionId]);

  const loadMessages = async () => {
    if (!currentSessionId) return;

    try {
      setLoading(true);
      const response = await api.get(`/chat/sessions/${currentSessionId}/messages`);

      if (response.success) {
        setMessages(response.messages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.created_at),
        })));
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const loadSession = async (sessionId: string) => {
    setCurrentSessionId(sessionId);
  };

  const createSession = async (title?: string) => {
    try {
      const response = await api.post('/chat/sessions', { title });

      if (response.success) {
        setCurrentSessionId(response.session.id);
        return response.session.id;
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create session');
      throw err;
    }
  };

  const sendMessage = async ({ content, attachments, voiceBlob, selectedPlatforms }: SendMessageParams) => {
    try {
      console.log('[useChat] sendMessage called with:', {
        content,
        hasAttachments: !!attachments,
        attachmentsLength: attachments?.length,
        hasVoiceBlob: !!voiceBlob,
        voiceBlobType: voiceBlob?.type,
        voiceBlobSize: voiceBlob?.size,
      });

      setLoading(true);
      setError(null);

      // Create session if none exists (but don't trigger message reload)
      let sessionId = currentSessionId;
      if (!sessionId) {
        try {
          const response = await api.post('/chat/sessions', { title: 'New Conversation' });
          if (response.success) {
            sessionId = response.session.id;
            // Set session ID without triggering useEffect reload
            setCurrentSessionId(sessionId);
          }
        } catch (err: any) {
          setError(err.response?.data?.error?.message || 'Failed to create session');
          throw err;
        }
      }

      // Add user message optimistically
      const userMessage: Message = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: content || '🎤 Voice message',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Prepare request payload
      let requestData: any;
      let axiosConfig: any = {};

      // If there's a voice blob or attachments, use FormData
      if (voiceBlob || (attachments && attachments.length > 0)) {
        console.log('[useChat] Using FormData for request');
        const formData = new FormData();

        // Always append content, even if empty (for voice-only messages)
        formData.append('content', content || '');
        console.log('[useChat] Appended content:', content || '');

        if (voiceBlob) {
          // Convert blob to file with proper name
          const voiceFile = new File([voiceBlob], 'voice-message.webm', {
            type: voiceBlob.type || 'audio/webm',
          });
          console.log('[useChat] Appending voice file:', {
            name: voiceFile.name,
            type: voiceFile.type,
            size: voiceFile.size,
          });
          formData.append('voice', voiceFile);
        }

        if (attachments && attachments.length > 0) {
          attachments.forEach((file) => {
            formData.append('attachments', file);
          });
        }

        if (selectedPlatforms && selectedPlatforms.length > 0) {
          formData.append('selectedPlatforms', JSON.stringify(selectedPlatforms));
        }

        requestData = formData;
        // IMPORTANT: Set Content-Type to undefined to let browser set multipart/form-data with boundary
        axiosConfig = {
          headers: {
            'Content-Type': undefined,
          },
          timeout: 120000, // 2 minutes timeout for AI processing
        };
        console.log('[useChat] Sending FormData request (Content-Type set to undefined for auto-detection)');
      } else {
        // Use JSON for text-only messages
        console.log('[useChat] Using JSON for text-only message');
        requestData = {
          content,
          selectedPlatforms: selectedPlatforms || [],
        };
        axiosConfig = {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 120000, // 2 minutes timeout for AI processing
        };
      }

      console.log('[useChat] About to send request to:', `/chat/sessions/${sessionId}/messages`);
      console.log('[useChat] Axios config:', axiosConfig);

      // Send to API
      const response = await api.post(
        `/chat/sessions/${sessionId}/messages`,
        requestData,
        axiosConfig
      );

      if (response.success) {
        // Add AI response
        const aiMessage: Message = {
          id: response.message.id,
          role: 'assistant',
          content: response.message.content,
          thinkingSteps: response.message.thinkingSteps,
          toolsUsed: response.message.toolsUsed,
          confidence: response.message.confidence,
          processingTime: response.message.processingTime,
          timestamp: new Date(),
          chartData: response.message.chartData,
          accessRestricted: response.message.accessRestricted,
        };
        setMessages((prev) => [...prev, aiMessage]);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send message');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    setCurrentSessionId(undefined);
  };

  /**
   * Send message with streaming response
   */
  const sendMessageWithStreaming = useCallback(async ({ content, selectedPlatforms }: SendMessageParams) => {
    try {
      setLoading(true);
      setError(null);
      setStreamingState({ isStreaming: true });

      // Create session if none exists
      let sessionId = currentSessionId;
      if (!sessionId) {
        const response = await api.post('/chat/sessions', { title: 'New Conversation' });
        if (response.success) {
          sessionId = response.session.id;
          setCurrentSessionId(sessionId);
        }
      }

      // Add user message
      const userMessage: Message = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      // Add placeholder AI message for streaming
      const aiMessageId = `streaming-${Date.now()}`;
      const aiMessage: Message = {
        id: aiMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        isStreaming: true,
      };
      setMessages((prev) => [...prev, aiMessage]);

      // Setup abort controller
      abortControllerRef.current = new AbortController();

      // Fetch with streaming
      const response = await fetch(`/api/chat/sessions/${sessionId}/messages/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content, selectedPlatforms }),
        credentials: 'include',
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error('Failed to start streaming');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No reader available');
      }

      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              switch (data.type) {
                case 'thinking':
                  setStreamingState({
                    isStreaming: true,
                    currentStep: data.reasoning,
                    stepNumber: data.step,
                  });
                  break;

                case 'token':
                  accumulatedContent += data.content;
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? { ...msg, content: accumulatedContent }
                        : msg
                    )
                  );
                  break;

                case 'complete':
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? {
                            ...msg,
                            id: data.metadata?.messageId || aiMessageId,
                            content: data.content,
                            isStreaming: false,
                            processingTime: data.metadata?.processingTime,
                            chartData: data.metadata?.chartData,
                          }
                        : msg
                    )
                  );
                  setStreamingState({ isStreaming: false });
                  break;

                case 'error':
                  setError(data.content);
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === aiMessageId
                        ? { ...msg, content: `Error: ${data.content}`, isStreaming: false }
                        : msg
                    )
                  );
                  setStreamingState({ isStreaming: false });
                  break;
              }
            } catch (e) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        setError(err.message || 'Failed to send message');
      }
    } finally {
      setLoading(false);
      setStreamingState({ isStreaming: false });
    }
  }, [currentSessionId]);

  /**
   * Cancel streaming response
   */
  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setStreamingState({ isStreaming: false });
    }
  }, []);

  return {
    messages,
    loading,
    error,
    currentSessionId,
    streamingState,
    sendMessage,
    sendMessageWithStreaming,
    cancelStreaming,
    loadSession,
    createSession,
    clearChat,
  };
}
